import React, { useState } from 'react';
import {
  useDatabaseTables,
  useTableSchema,
  useExecuteQuery,
  TableInfo,
  QueryResultRow,
} from '../helpers/useDatabaseApi';
import { usePublishedSites, useManageSiteDatabase } from '../helpers/useCloudflareApi';
import { Button } from './Button';
import { Badge } from './Badge';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { Textarea } from './Textarea';
import {
  Database,
  Table,
  Play,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  AlertCircle,
  Info,
  Copy,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import styles from './DatabasePanel.module.css';

interface DatabasePanelProps {
  sessionId: string;
  isOwner?: boolean;
}

export const DatabasePanel: React.FC<DatabasePanelProps> = ({ sessionId, isOwner = false }) => {
  const { data: sitesData, isLoading: sitesLoading } = usePublishedSites(sessionId);
  const manageDatabaseMutation = useManageSiteDatabase();
  const executeQueryMutation = useExecuteQuery();

  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [sqlQuery, setSqlQuery] = useState('SELECT * FROM users LIMIT 10;');
  const [queryResults, setQueryResults] = useState<QueryResultRow[] | null>(null);
  const [queryMeta, setQueryMeta] = useState<{
    duration: number;
    rowsRead: number;
  } | null>(null);
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [enableDbDialogOpen, setEnableDbDialogOpen] = useState(false);
  const [copiedSql, setCopiedSql] = useState(false);

  const sites = sitesData?.sites || [];
  const sitesWithDb = sites.filter((s) => s.hasDatabase && s.d1DatabaseId);
  const sitesWithoutDb = sites.filter((s) => !s.hasDatabase);

  // Auto-select first site with database
  React.useEffect(() => {
    if (!selectedSiteId && sitesWithDb.length > 0) {
      setSelectedSiteId(sitesWithDb[0].id);
    }
  }, [sitesWithDb, selectedSiteId]);

  const {
    data: tablesData,
    isLoading: tablesLoading,
    refetch: refetchTables,
  } = useDatabaseTables(selectedSiteId);

  const { data: schemaData, isLoading: schemaLoading } = useTableSchema(
    selectedSiteId,
    selectedTable,
  );

  const handleEnableDatabase = async (siteId: number) => {
    try {
      await manageDatabaseMutation.mutateAsync({ siteId, action: 'enable' });
      toast.success('Database enabled');
      setSelectedSiteId(siteId);
      setEnableDbDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to enable database');
    }
  };

  const handleExecuteQuery = async () => {
    if (!selectedSiteId || !sqlQuery.trim()) return;

    try {
      const result = await executeQueryMutation.mutateAsync({
        siteId: selectedSiteId,
        sql: sqlQuery.trim(),
      });
      setQueryResults(result.results);
      setQueryMeta({
        duration: result.meta.duration,
        rowsRead: result.meta.rowsRead,
      });
      toast.success(`Query executed in ${result.meta.duration.toFixed(2)}ms`);

      // Refresh tables if it was a DDL statement
      const upperSql = sqlQuery.toUpperCase().trim();
      if (
        upperSql.startsWith('CREATE') ||
        upperSql.startsWith('DROP') ||
        upperSql.startsWith('ALTER')
      ) {
        refetchTables();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Query failed');
      setQueryResults(null);
      setQueryMeta(null);
    }
  };

  const toggleTableExpanded = (tableName: string) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName);
    } else {
      newExpanded.add(tableName);
    }
    setExpandedTables(newExpanded);
    setSelectedTable(tableName);
  };

  const handleCopySql = () => {
    navigator.clipboard.writeText(sqlQuery);
    setCopiedSql(true);
    setTimeout(() => setCopiedSql(false), 2000);
  };

  const insertTableQuery = (tableName: string) => {
    setSqlQuery(`SELECT * FROM "${tableName}" LIMIT 100;`);
  };

  if (sitesLoading) {
    return (
      <div className={styles.container}>
        <Skeleton style={{ width: '100%', height: '200px' }} />
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Database size={32} className={styles.emptyIcon} />
          <p className={styles.emptyText}>No published sites</p>
          <p className={styles.emptySubtext}>
            Publish a site first, then enable a database for it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>
            <Database size={18} />
            Database Manager
          </h3>
        </div>

        <div className={styles.siteSelector}>
          {sitesWithDb.length > 0 ? (
            <Select
              value={selectedSiteId?.toString() || ''}
              onValueChange={(v) => {
                setSelectedSiteId(parseInt(v, 10));
                setSelectedTable(null);
                setQueryResults(null);
              }}
            >
              <SelectTrigger className={styles.selectTrigger}>
                <SelectValue placeholder="Select a database..." />
              </SelectTrigger>
              <SelectContent>
                {sitesWithDb.map((site) => (
                  <SelectItem key={site.id} value={site.id.toString()}>
                    {site.subdomain} ({site.d1DatabaseName})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className={styles.noDbMessage}>
              <Info size={14} />
              <span>No databases enabled yet</span>
            </div>
          )}

          {sitesWithoutDb.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setEnableDbDialogOpen(true)}>
              <Plus size={14} />
              Enable Database
            </Button>
          )}
        </div>
      </div>

      {selectedSiteId && tablesData && (
        <div className={styles.mainContent}>
          {/* Tables Sidebar */}
          <div className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <span>Tables</span>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => refetchTables()}
                title="Refresh tables"
              >
                <RefreshCw size={14} />
              </Button>
            </div>

            {tablesLoading ? (
              <Skeleton style={{ width: '100%', height: '100px' }} />
            ) : tablesData.tables.length === 0 ? (
              <div className={styles.noTables}>
                <Table size={20} />
                <span>No tables yet</span>
              </div>
            ) : (
              <div className={styles.tableList}>
                {tablesData.tables.map((table) => (
                  <div key={table.name} className={styles.tableItem}>
                    <button
                      className={`${styles.tableButton} ${selectedTable === table.name ? styles.selected : ''}`}
                      onClick={() => toggleTableExpanded(table.name)}
                    >
                      {expandedTables.has(table.name) ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                      <Table size={14} />
                      <span className={styles.tableName}>{table.name}</span>
                      {table.rowCount !== undefined && (
                        <Badge variant="secondary" className={styles.rowCount}>
                          {table.rowCount}
                        </Badge>
                      )}
                    </button>

                    {expandedTables.has(table.name) &&
                      schemaData &&
                      selectedTable === table.name && (
                        <div className={styles.tableSchema}>
                          {schemaLoading ? (
                            <Skeleton style={{ width: '100%', height: '60px' }} />
                          ) : (
                            <>
                              {schemaData.columns.map((col) => (
                                <div key={col.cid} className={styles.columnItem}>
                                  <span className={styles.columnName}>{col.name}</span>
                                  <span className={styles.columnType}>{col.type}</span>
                                  {col.pk === 1 && <Badge variant="outline">PK</Badge>}
                                </div>
                              ))}
                              <Button
                                variant="ghost"
                                size="sm"
                                className={styles.queryTableBtn}
                                onClick={() => insertTableQuery(table.name)}
                              >
                                Query table
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Query Panel */}
          <div className={styles.queryPanel}>
            <div className={styles.queryHeader}>
              <span>SQL Query</span>
              <div className={styles.queryActions}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon-sm" onClick={handleCopySql}>
                      {copiedSql ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy SQL</TooltipContent>
                </Tooltip>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleExecuteQuery}
                  disabled={executeQueryMutation.isPending || !sqlQuery.trim()}
                >
                  {executeQueryMutation.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <Play size={14} />
                      Run
                    </>
                  )}
                </Button>
              </div>
            </div>

            <Textarea
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value)}
              className={styles.sqlInput}
              placeholder="Enter SQL query..."
              rows={4}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleExecuteQuery();
                }
              }}
            />

            {queryMeta && (
              <div className={styles.queryMeta}>
                <span>Duration: {queryMeta.duration.toFixed(2)}ms</span>
                <span>Rows read: {queryMeta.rowsRead}</span>
              </div>
            )}

            {/* Results */}
            <div className={styles.resultsContainer}>
              {executeQueryMutation.isPending ? (
                <div className={styles.loadingResults}>
                  <Spinner />
                  <span>Executing query...</span>
                </div>
              ) : queryResults ? (
                queryResults.length === 0 ? (
                  <div className={styles.noResults}>
                    <Info size={20} />
                    <span>Query returned no results</span>
                  </div>
                ) : (
                  <div className={styles.resultsTable}>
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(queryResults[0]).map((key) => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {queryResults.map((row, idx) => (
                          <tr key={idx}>
                            {Object.values(row).map((value, vidx) => (
                              <td key={vidx}>
                                {value === null ? (
                                  <span className={styles.nullValue}>NULL</span>
                                ) : typeof value === 'object' ? (
                                  JSON.stringify(value)
                                ) : (
                                  String(value)
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className={styles.noResults}>
                  <Database size={20} />
                  <span>Run a query to see results</span>
                  <span className={styles.hint}>Ctrl+Enter to execute</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enable Database Dialog */}
      <Dialog open={enableDbDialogOpen} onOpenChange={setEnableDbDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enable Database</DialogTitle>
            <DialogDescription>
              Choose a published site to enable a D1 database for. The database will be available at
              runtime in your deployed site.
            </DialogDescription>
          </DialogHeader>
          <div className={styles.enableDbList}>
            {sitesWithoutDb.map((site) => (
              <button
                key={site.id}
                className={styles.enableDbItem}
                onClick={() => handleEnableDatabase(site.id)}
                disabled={manageDatabaseMutation.isPending}
              >
                <Database size={16} />
                <span>{site.subdomain}</span>
                {manageDatabaseMutation.isPending && <Spinner size="sm" />}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEnableDbDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
